import "quill-paste-smart"; // this plugin registers itself, it is sufficient to just import it
import { FC, useRef } from "react";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import ReactQuill, { Quill } from "react-quill";

import "react-quill/dist/quill.snow.css";

import { formatUrl } from "shared/utils";
import { FieldWrapper, FieldWrapperProps } from "../FieldWrapper";
import { Toolbar, ToolbarItems } from "././components";
import { RTEContainer, StyledReactQuill } from "./styles";
import { sanitizeHtml } from "./utils";

// adds http:// in front of the url
const Link = Quill.import("formats/link");
Link.sanitize = (val: string) => formatUrl(val);

interface RTEProps extends FieldWrapperProps {
  control: Control;
  validation?: RegisterOptions;
  defaultValue?: string;
  removeOptions?: ("list" | "link" | "inline")[];
  onChange?: (value: string | null) => void;
  excludeItems?: { [key in ToolbarItems]?: boolean };
  size?: "s" | "m";
}

export const RTE: FC<RTEProps> = ({
  control,
  validation,
  defaultValue,
  name,
  error,
  onChange: triggerOnChange,
  excludeItems,
  size = "m",
  ...props
}) => {
  const editorRef = useRef<ReactQuill | null>();

  const toolbarId = `${name.replace(".", "")}-toolbar`;

  return (
    <FieldWrapper name={name} error={error} {...props}>
      <RTEContainer error={!!error} id={name} size={size}>
        <Toolbar id={toolbarId} excludeItems={excludeItems} />
        <Controller
          control={control}
          name={name}
          rules={validation}
          defaultValue={defaultValue}
          render={({ onChange, onBlur }) => (
            <StyledReactQuill
              ref={(ref) => {
                editorRef.current = ref;
              }}
              theme="snow"
              defaultValue={defaultValue}
              onChange={(value) => {
                const sanitizedValue = sanitizeHtml(value);

                onChange(sanitizedValue || "");
                triggerOnChange && triggerOnChange(sanitizedValue);
                onBlur();
              }}
              modules={{
                toolbar: {
                  container: `#${toolbarId}`,
                },
              }}
              formats={["link", "list", "bold", "italic", "background"]}
            />
          )}
        />
      </RTEContainer>
    </FieldWrapper>
  );
};
